// Pure helpers for "what is still deliverable" on a Modern Trade sale order.
// Owner ruling 2026-11-04: an SO may carry several delivery notes (the factory
// delivers in parts). The backend (MtDeliveryCreate) has the final word: it
// caps each DO by received lot minus earlier non-cancelled DOs (lot mode), or
// by SO line qty minus delivered qty (no-lot mode). These helpers only decide
// whether to offer the button and what to pre-fill. Vue-free for jest.

const DELIVERY_CANCELLED = 9;
const SO_CLOSED = 6;
const SO_CANCELLED = 9;
const SHIP_PENDING = 1;

// Σ delivered qty per product over non-cancelled delivery notes.
export function deliveredByProduct(deliveries, deliveryItems) {
  const cancelled = {};
  (deliveries || []).forEach(d => {
    if (d && Number(d.status) === DELIVERY_CANCELLED) cancelled[d.id] = true;
  });
  const out = {};
  (deliveryItems || []).forEach(di => {
    if (!di || cancelled[di.delivery_id]) return;
    out[di.product_id] = (out[di.product_id] || 0) + (Number(di.qty) || 0);
  });
  return out;
}

// No-lot mode (outright type 1/3): one row per SO product with the qty still
// to deliver = Σ SO line qty − Σ delivered (never below 0). SO line order.
export function productRemainingRows(items, deliveries, deliveryItems) {
  const delivered = deliveredByProduct(deliveries, deliveryItems);
  const rows = [];
  const byProduct = {};
  (items || []).forEach(i => {
    if (!i) return;
    let r = byProduct[i.product_id];
    if (!r) {
      r = { product_id: i.product_id, product_code: i.product_code, product_name: i.product_name, ordered: 0 };
      byProduct[i.product_id] = r;
      rows.push(r);
    }
    r.ordered += Number(i.qty) || 0;
  });
  rows.forEach(r => {
    r.delivered = delivered[r.product_id] || 0;
    r.remaining = Math.max(0, r.ordered - r.delivered);
  });
  return rows;
}

// Lot mode (consignment / outright factory): lots with qty_available > 0
// (qty_available comes from the BE and already nets out earlier DOs).
export function deliverableLots(receivedLots) {
  return (receivedLots || []).filter(l => l && Number(l.qty_available) > 0);
}

// Whether "create delivery" is offered: the SO is confirmed or later, not
// closed / cancelled, and either it has no delivery note yet (first DO, the
// old rule) or something is still deliverable.
export function canCreateDelivery({ canMutate, status, needsLot, deliveries, deliveryItems, receivedLots, items }) {
  const st = Number(status);
  if (!canMutate || !(st >= 2) || st === SO_CLOSED || st === SO_CANCELLED) return false;
  if (!(deliveries || []).length) return true;
  if (needsLot) return deliverableLots(receivedLots).length > 0;
  return productRemainingRows(items, deliveries, deliveryItems).some(r => r.remaining > 0);
}

// Close with goods still owed (owner 2026-11-04): products whose Σ ordered
// (every SO line of the product) exceeds Σ delivered on live DOs. The BE
// (MtSaleOrderClose, 409 outstanding_items) is the final word; this only
// decides whether to show the "close anyway?" list up front.
export function outstandingRows(items, deliveries, deliveryItems) {
  return productRemainingRows(items, deliveries, deliveryItems).filter(r => r.remaining > 0);
}

// Whether the "cancel delivery note" button is offered on one DO row. The BE
// (POST /deliveries/:id/cancel) re-checks everything and also refuses a DO
// whose BO order is live / cut stock or whose consignment goods were sold —
// the screen only hides the button where the answer is already known.
export function canCancelDelivery({ canMutate, soStatus, delivery, hasLiveOrder }) {
  if (!canMutate || !delivery) return false;
  const st = Number(soStatus);
  if (st === SO_CLOSED || st === SO_CANCELLED) return false;
  if (Number(delivery.status) === DELIVERY_CANCELLED) return false;
  if (delivery.invoice_id) return false;
  // Round 2 (owner): only a DO still "รอส่งของ" (ship_status 1; 0/empty =
  // never set) may be cancelled — shipped or any later state is final.
  if (Number(delivery.ship_status) > SHIP_PENDING) return false;
  return !hasLiveOrder;
}
